import { Icon } from '../components/icon';
import { focusRef } from '../helpers/focus';
import { Component, getQueryVariable, getRandomInt, linkify, removeFromArray } from '../helpers/helper';
import { VTitle } from '../helpers/vtitle';
import { service } from '../service';
import { state, store_url } from '../state';
import { store_api } from '../storeAPI';
import { router } from '../router';
import { authReady } from '../firebase';
import { transport } from '../firebaseTransport';
import { onApiMessage } from '../app';
import { metrics } from '../metrics';
import { AgeGateDlg, ageGateRequired } from './ageGate';
import * as leoProfanity from 'leo-profanity';

interface IChannelUser {
	name: string;
}

class LState {
	channelId: string = null;
	loginError: string = null;
	successMsg: string = null;
	newChannel = false;
	baseUrl: string = null;
	user: IChannelUser = { name: '' };

	setChannelIdFromUrl() {
		let parts = document.location.pathname.split('/').reverse();
		removeFromArray('', parts);
		if (parts[0] && parts[0] !== 'client') {
			this.channelId = parts[0];
		}
	}

	async init() {
		let user: IChannelUser = JSON.parse(localStorage.getItem('user'));
		if (user) {
			this.user.name = user.name || '';
			state.firstTimeUser = false;
		}

		if (state.app_id) {
			state.appInfo = await store_api.getApp(state.app_id);
		}

		this.baseUrl = document.location.origin;
		if (document.location.pathname.includes('/client')) {
			this.baseUrl += '/client';
		}

		this.setChannelIdFromUrl();
		if (!this.channelId) {
			this.createNewChannel();
		}

		let autoJoin = getQueryVariable('auto-join');
		if (autoJoin) {
			this.user.name = 'auto_join_' + Math.floor(Math.random() * 1000);
			setTimeout(() => {
				this.join();
			}, 500);
		}
		// else if (this.user.session_id) {
		// 	this.join();
		// }
	}

	copyChannelUrl() {
		let el: HTMLInputElement = document.querySelector('#newChannelUrl');
		el.classList.add('highlight');
		navigator.clipboard.writeText(this.newChannelUrl);
		setTimeout(() => {
			el.classList.remove('highlight');
		}, 500);
	}

	get newChannelUrl() {
		return this.baseUrl + '/' + this.channelId;
	}

	cancelNewRoom() {
		this.newChannel = false;
		this.setChannelIdFromUrl();
	}

	async createNewChannel() {
		this.loginError = null;
		this.channelId = null;
		this.newChannel = true;
	}

	joinChannel() {
		if (!this.channelId) {
			this.loginError = 'Enter room link or name';
			return;
		}
		if (this.user.name && leoProfanity.check(this.user.name)) {
			this.loginError = 'Please choose a different username.';
			return;
		}
		this.join();
	}

	join() {
		state.loadingChannel = true;
		this._join().finally(() => {
			state.loadingChannel = false;
		});
	}

	async _join() {
		if (this.newChannel) {
			this.channelId = await service.getUniqueChannelName();
			if (state.appInfo) {
				state.app_id = state.appInfo.id;
			}
		}
		state.removeQueryFromUrl();
		await state.apiInitPromise;

		let channelId = this.channelId.trim();

		let user = this.user;
		if (!user.name) {
			user.name = 'Guest' + getRandomInt(10, 99);
		}

		// Resolve the Firebase auth uid up front so we can record a uid → IP
		// mapping server-side for ban-by-uid lookups.
		const fbUserPre = await authReady;

		let form: IJoinChannel = {
			name: user.name,
			client_id: null,
			email: null,
			password: null,
			session_id: null,
			channel_id: channelId,
			api_key: '5a05e3ea-59ea-4213-a321-220174478fee',
			audioEnabled: state.audioEnabled,
			uid: fbUserPre.uid
		};

		let r = await service.joinChannel(form);

		if (r.success) {
			localStorage.setItem('user', JSON.stringify(user));
			this.channelId = channelId;

			const fbUser = fbUserPre;

			state.channel_id = channelId;
			state.user = { id: fbUser.uid, name: user.name } as any;
			state.connected = true;

			try {
				await transport.joinChannel(channelId, { name: user.name });
				metrics.user = user.name;
				metrics.channels.push(channelId);
				metrics.location = document.location.href;
				metrics.enterDate = new Date();
				transport.onMembersChanged(members => {
					const list = Object.values(members || {}).map((m: any) => ({
						id: m.uid,
						name: m.name || 'Guest'
					})) as IUser[];
					state.updateUsers(list);

					// If our own member entry has disappeared (e.g. host kicked
					// us via /ban_user), force-disconnect locally.
					if (
						state.connected &&
						state.channel_id &&
						fbUser.uid &&
						!list.some(u => u.id === fbUser.uid)
					) {
						console.warn('[transport] self removed from members — disconnecting');
						state.handleKickedFromChannel();
					}
				});
				transport.onMessage(m => {
					// Legacy chat rendering — kept for a nicer in-dialog UX.
					if (m.type === 'post_message') {
						const payload = (m as any).message || {};
						const text = typeof payload === 'string' ? payload : payload.text;
						if (text) {
							const from = payload.from_name || m.from;
							state.messages.push({ from, text, url: linkify(text) });
							Vue.nextTick(() => {
								const chat = document.querySelector('.chat_messages');
								if (chat) chat.scrollTop = chat.scrollHeight;
							});
						}
						return;
					}
					// Everything else (activate_app, close_app, captions, etc.)
					// routes through the legacy handler so in-channel features
					// keep working over the RTDB transport.
					onApiMessage(m as any).catch(err => console.error('onApiMessage error', err));
				});
				transport.onSignal(sig => {
					// Re-shape SignalingMessage into the legacy onApiMessage envelope:
					// { type, peer_id, offer|answer|ice_candidate, ... } so webRTC.ts
					// dispatch logic in onApiMessage stays untouched.
					const flat = { type: sig.type, ...(sig.payload || {}) };
					onApiMessage(flat as any).catch(err => console.error('onSignal dispatch error', err));
				});
				transport.onOwnerChanged(ownerId => {
					state.channelOwner = ownerId;
				});
				transport.onRoomLockedChanged(locked => {
					state.roomLocked = locked;
				});
				transport.onAttentionChanged(signals => {
					state._attentionSignals = signals as any;
					state.users.forEach(u => {
						u.attentionSignal = (signals as any)[u.id] ?? null;
					});
				});
			} catch (e: any) {
				console.error('transport.joinChannel failed', e);
				const isLocked = String(e).includes('PERMISSION_DENIED') || e?.code === 'PERMISSION_DENIED';
				this.loginError = isLocked ? 'This room is currently locked.' : 'Failed to connect. Please try again.';
				state.channel_id = null;
				state.connected = false;
				state.loadingChannel = false;
				return;
			}

			// Auto-activate the app the user picked before creating the room.
			if (state.app_id) {
				const info = state.appInfo;
				try {
					await transport.broadcast({
						type: 'activate_app',
						appId: state.app_id,
						name: info?.name,
						url: info?.launch_url,
						image: info?.tile_image,
						includeSelf: true
					} as any);
				} catch (err) {
					console.error('initial activate_app failed', err);
				}
				state.app_id = null;
			}

			router.push('/' + channelId);

			if (this.newChannel) {
				state.showShareDlg = true;
			}
		} else if (r.error) {
			this.loginError = r.error;
		}
	}
}

const AppTile = Component((p: { app: IAppInfo }) => {
	return () => (
		<div class={{ appImage: true }} onClick={e => state.featured_apps.forEach(a => (state.appInfo = p.app))}>
			<img src={store_url + p.app.tile_image} />
			<h1>{p.app.name}</h1>
		</div>
	);
});

const Form = Component((p: { l: LState }) => {
	const l = p.l;
	return () => (
		<table>
			<tr>
				<td>
					<label for="name">Your name</label>
				</td>
				<td>
					<input
						id="name"
						ref={focusRef()}
						value={l.user.name}
						onChange={e => (l.user.name = e.target.value)}
						onKeydown={e => e.key === 'Enter' && l.join()}
						spellcheck="false"
					/>
				</td>
			</tr>

			{!l.newChannel && (
				<tr>
					<td>
						<label>Room</label>
					</td>
					<td>
						<input
							class="input-join"
							value={l.channelId}
							placeholder="Enter room link or name"
							onChange={e => (l.channelId = e.target.value)}
						/>
					</td>
				</tr>
			)}
			{l.newChannel && (
				<tr style="display: none">
					<td>
						<label style="flex-shrink: 0">Share Link</label>
					</td>
					<td>
						<div style="display: flex; align-items: center">
							<input class="input-join" readonly id="newChannelUrl" value={l.newChannelUrl} />
							<button class="button share" onClick={e => l.copyChannelUrl()}>
								<Icon name="copy" />
								Copy
							</button>
						</div>
					</td>
				</tr>
			)}
		</table>
	);
});

export const Home = Component(() => {
	const l = Vue.reactive(new LState());
	l.init();

	const gate = Vue.reactive({ show: false });
	let pendingAction: (() => void) | null = null;
	function onGateAccept() {
		gate.show = false;
		const act = pendingAction;
		pendingAction = null;
		if (act) act();
	}
	function requireAge(action: () => void) {
		if (ageGateRequired()) {
			pendingAction = action;
			gate.show = true;
		} else {
			action();
		}
	}

	function noApps() {
		state.appInfo = null;
	}

	return () => (
		<div
			style={{
				height: '100%',
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				background:
					'radial-gradient(circle at 18% 18%, rgba(102, 126, 234, 0.35), transparent 50%),' +
					'radial-gradient(circle at 82% 78%, rgba(180, 80, 200, 0.28), transparent 55%),' +
					'#0c0e1a'
			}}
		>
			{!state.loadingChannel && (
				<div id="login-dlg" class="client">
					<div class="welcome">
						<Icon name="ga-logo" size="3rem" />
						<div>
							<div class="desc">Free meeting platform with apps!</div>
							{/* {state.firstTimeUser && l.newChannel && (
								<div style="display:flex;flex-direction:column;gap:6px;margin-top: 6px;font-size: 14px;color: #e5b2f5;">
									<div>✶ No Time Limits</div>
									<div>✶ Share audio, video, and screens</div>
									<div>✶ Collaborative apps</div>
								</div>
							)} */}
						</div>
					</div>
					<div style={{ padding: '1em' }}>
						{l.newChannel && (
							<div style="display:flex; gap: 2em;justify-content: center;color: springgreen;margin-bottom: 1em;font-size: 18px;">
								<span>Select an app to use in your room: </span>
							</div>
						)}

						<div style={{ display: 'flex', gap: '20px', alignItems: 'center', justifyContent: 'center' }}>
							{l.newChannel && state.appInfo && (
								<button class="side-app" onClick={noApps}>
									<VTitle text="No App" />
									<Icon name="times-solid" />
								</button>
							)}
							{state.appInfo && <AppTile app={state.appInfo} />}
							{!state.appInfo && l.newChannel && state.featured_apps.map(a => <AppTile app={a} />)}
							{l.newChannel && (
								<button
									class="side-app"
									onClick={e => {
										//state.browseApps();
										router.push('/apps');
									}}
								>
									<VTitle text="More Apps" />
									<Icon name="search" />
								</button>
							)}
						</div>

						<Form l={l} />

						<transition name="fade">
							{l.loginError && (
								<div class="form-row horizontal" id="login-error">
									{l.loginError}
								</div>
							)}
						</transition>
						{l.successMsg && (
							<div class="form-row horizontal" id="success-message">
								{l.successMsg}
							</div>
						)}

						{l.newChannel && (
							<div class="form-buttons">
								<button class="button secondary" onClick={e => l.cancelNewRoom()}>
									Join a Room
								</button>
								<button class="button accent" onClick={e => requireAge(() => l.join())}>
									Create Room
								</button>
							</div>
						)}
						{!l.newChannel && (
							<div class="form-buttons">
								<button class="button secondary" onClick={e => l.createNewChannel()}>
									Create New Room
								</button>
								<button class="button accent" onClick={e => requireAge(() => l.joinChannel())}>
									Join Room
								</button>
							</div>
						)}
						<div class="form-privacy">By joining/creating a room you agree to our <a href="/terms_of_service">Terms of Service</a> and <a href="/privacy_policy">Privacy Policy</a>.</div>
						<div class="form-privacy-note">
							GatherAct is an unmoderated technology demo. It is not designed for, directed at,
							or monitored for use by children.
						</div>
					</div>
				</div>
			)}
			{gate.show && <AgeGateDlg onAccept={onGateAccept} />}
			<div></div>
		</div>
	);
});
